/**
 * HubAnnouncements — the Olive hub announcements banner (Phase X.5.1).
 *
 * Read-only for everyone with hub access (view_staff_tools); the add /
 * delete affordance renders only for manage_announcements holders (owner /
 * data_steward / exec_support). The server is authoritative on both ends:
 * the GET returns the already-computed active list (expired non-recurring
 * rows dropped, annual rows rolled forward, sorted), and writes re-derive
 * manage_announcements from ctx.role. This component never re-sorts or
 * re-expires; it renders the server order as-is.
 *
 * The "↻ yearly" recurrence marker shows ONLY in the manage/edit view (it
 * is editorial metadata, not reader-facing), matching the approved
 * prototype. When the user has hub access but there is nothing to show and
 * cannot manage, the banner renders nothing (no empty card for staff).
 *
 * Birthdays and workiversaries are roster-derived rows (source='roster',
 * migration 0202): the server computes them from the People page's dates on
 * every read, so they carry no delete control. In the edit view their
 * marker reads "from People", which is where a wrong date gets fixed.
 */

import { useState, type CSSProperties, type ReactNode } from 'react';
import { useTenant } from '@/lib/auth';
import { canManageAnnouncements, canViewStaffTools } from '@/lib/capabilities';
import {
  useAnnouncements,
  useCreateAnnouncement,
  useDeleteAnnouncement,
} from '@/lib/announcements';
import { contactSentence, sessionExpiredText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  type AnnouncementType,
  type AnnouncementRecurrence,
  type HubRowType,
} from '@/lib/api';
import { SelectField } from '@/components/primitives/SelectField';
import { DateInput } from '@/components/primitives/DateInput';

interface TypeStyle {
  label: string;
  bg: string;
  fg: string;
}

// UX sweep 2026-07-10: the category chips read from the brand families
// (the raw off-palette hexes - holiday blue, birthday pink - retired).
// Identity hues only; "Office closed" is the one operational-attention
// category, so it alone takes the pepper family.
const TYPE_STYLES: Record<HubRowType, TypeStyle> = {
  event: { label: 'Event', bg: 'var(--color-sage-50)', fg: 'var(--color-sage-600)' },
  birthday: { label: 'Birthday', bg: 'var(--color-warm-100)', fg: 'var(--color-warm-700)' },
  anniversary: { label: 'Anniversary', bg: 'var(--color-tick-affirm)', fg: 'var(--color-affirm-deep)' },
  holiday: { label: 'Holiday', bg: 'var(--color-sand)', fg: 'var(--color-ink-soft)' },
  closed: { label: 'Office closed', bg: 'var(--color-pepper-soft)', fg: 'var(--color-pepper-deep)' },
  // Roster-derived only (a start date inside the window); never offered
  // in the add form, since nothing stored may carry it.
  first_day: { label: 'First day', bg: 'var(--color-sage-100)', fg: 'var(--color-sage-700)' },
};

/** The types someone can write: the stored vocabulary, not the derived one. */
const TYPE_KEYS = (Object.keys(TYPE_STYLES) as HubRowType[]).filter(
  (k): k is AnnouncementType => k !== 'first_day',
);

// X.5.2 — recurrence dropdown. 'annual' is the stored value; the UI labels
// it "Yearly". 'none' = does not repeat.
const RECURRENCE_OPTIONS: ReadonlyArray<{ value: AnnouncementRecurrence; label: string }> = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'annual', label: 'Yearly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'biweekly', label: 'Every two weeks' },
  { value: 'weekly', label: 'Weekly' },
];

// Short label for the manage-view "↻ {period}" recurrence marker.
const RECURRENCE_MARKER: Record<Exclude<AnnouncementRecurrence, 'none'>, string> = {
  annual: 'yearly',
  monthly: 'monthly',
  biweekly: 'every two weeks',
  weekly: 'weekly',
};

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** 'YYYY-MM-DD' -> 'Mon D' without timezone drift; '' for null/blank. */
function formatDate(iso: string | null): string {
  if (!iso) return '';
  const parts = iso.split('-').map(Number);
  const month = parts[1];
  const day = parts[2];
  if (!month || !day || month < 1 || month > 12) return '';
  return `${MONTHS[month - 1]} ${day}`;
}

function MegaphoneIcon(): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1zM14 8a4 4 0 0 1 0 8M16 5a8 8 0 0 1 0 14" />
    </svg>
  );
}

const PILL_BASE =
  'whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide';

export function HubAnnouncements(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const hasHubAccess = canViewStaffTools(whoami);
  const canManage = canManageAnnouncements(whoami);

  const { announcements, isLoading } = useAnnouncements(tenantSlug, { enabled: hasHubAccess });
  const createMutation = useCreateAnnouncement(tenantSlug);
  const deleteMutation = useDeleteAnnouncement(tenantSlug);
  const contact = useSupportContact();

  const [editMode, setEditMode] = useState(false);
  const [type, setType] = useState<AnnouncementType>('event');
  const [text, setText] = useState('');
  const [date, setDate] = useState('');
  const [recurrence, setRecurrence] = useState<AnnouncementRecurrence>('none');

  // No hub access at all → no banner.
  if (!hasHubAccess) return null;
  // Nothing to show and cannot add → don't render an empty card for staff.
  if (announcements.length === 0 && !canManage) return null;

  const resetForm = (): void => {
    setText('');
    setDate('');
    setRecurrence('none');
    setType('event');
  };

  const trimmed = text.trim();
  // Every recurring value needs an anchor date (mirrors the server CHECK).
  const addDisabled =
    trimmed.length === 0 || (recurrence !== 'none' && date === '') || createMutation.isPending;

  const handleAdd = (): void => {
    if (addDisabled) return;
    createMutation.mutate(
      {
        type,
        body: trimmed,
        event_date: date === '' ? null : date,
        recurrence,
      },
      { onSuccess: resetForm },
    );
  };

  // X.5.4 — surface write failures instead of swallowing them. The api
  // client (src/lib/api.ts) bounces an expired SWA session to login on its
  // own; here we render the matching message (and a generic one for any
  // other failure) so a failed Add/Delete is never a silent no-op.
  const writeError = createMutation.error ?? deleteMutation.error;

  return (
    <section
      data-testid="hub-announcements"
      aria-label="Company announcements"
      className="rounded-card bg-paper px-5 py-[18px] shadow-sh2"
    >
      <div className="mb-1.5 flex items-center gap-2.5">
        <span className="flex h-[30px] w-[30px] items-center justify-center rounded-[9px] bg-warm-400/[16%] text-warm-500">
          <MegaphoneIcon />
        </span>
        <h3 className="flex-1 font-display text-[16px] font-semibold text-sage-800">Around the office</h3>
        {canManage && (
          <button
            type="button"
            data-testid="hub-announcements-edit-toggle"
            onClick={() => setEditMode((v) => !v)}
            aria-pressed={editMode}
            className={
              editMode
                ? 'inline-flex items-center gap-1.5 rounded-full border border-sage-800 bg-sage-800 px-3 py-1 text-xs font-semibold text-white'
                : 'inline-flex items-center gap-1.5 rounded-full border border-sage-300/50 bg-white px-3 py-1 text-xs font-semibold text-sage-600 hover:bg-sage-50'
            }
          >
            {editMode ? 'Done' : 'Edit'}
          </button>
        )}
      </div>

      {writeError && (
        <p
          role="alert"
          data-testid="hub-announcements-error"
          className="mb-2 rounded-md border border-warm-300 bg-warm-50 px-3 py-2 text-[13px] text-warm-700"
        >
          {sessionExpiredText(writeError) ??
            contactSentence("Your announcement didn't save. Try again, and if it happens again, tell {contact_system}.", contact)}
        </p>
      )}

      {announcements.map((a) => {
        const style = TYPE_STYLES[a.type] ?? TYPE_STYLES.event;
        const pillStyle: CSSProperties = { backgroundColor: style.bg, color: style.fg };
        return (
          <div
            key={a.id}
            data-testid={`hub-announcement-item-${a.id}`}
            className="flex items-center gap-3 border-t border-sage-300/20 py-2.5 first:border-t-0"
          >
            <span className={PILL_BASE} style={pillStyle}>
              {style.label}
            </span>
            <span className="flex-1 text-[13.5px] text-ink">{a.body}</span>
            {a.effective_date && (
              <span className="whitespace-nowrap text-xs font-medium text-sage-400">
                {formatDate(a.effective_date)}
              </span>
            )}
            {editMode && a.source === 'roster' && (
              <span
                data-testid={`hub-announcement-source-${a.id}`}
                className="whitespace-nowrap text-xs text-sage-300"
                aria-label="From the People page; edit the date there"
              >
                from People
              </span>
            )}
            {editMode && a.source !== 'roster' && a.recurrence !== 'none' && (
              <span
                className="whitespace-nowrap text-xs text-sage-300"
                aria-label={`Repeats ${RECURRENCE_MARKER[a.recurrence]}`}
              >
                {`↻ ${RECURRENCE_MARKER[a.recurrence]}`}
              </span>
            )}
            {editMode && a.source !== 'roster' && (
              <button
                type="button"
                data-testid={`hub-announcement-delete-${a.id}`}
                onClick={() => deleteMutation.mutate(a.id)}
                disabled={deleteMutation.isPending}
                aria-label="Delete announcement"
                className="rounded-md px-1.5 text-lg leading-none text-sage-300 hover:bg-pepper-soft hover:text-pepper-deep disabled:opacity-50"
              >
                {'×'}
              </button>
            )}
          </div>
        );
      })}

      {announcements.length === 0 && !isLoading && (
        <p className="py-1.5 text-[13px] text-sage-400">When the office posts an announcement, you'll see it here.</p>
      )}

      {editMode && canManage && (
        <div
          data-testid="hub-announcement-add"
          className="mt-3 flex flex-wrap items-center gap-2 border-t border-dashed border-sage-300/45 pt-3"
        >
          <SelectField size="compact"
            data-testid="hub-announcement-type"
            value={type}
            onChange={(e) => setType(e.target.value as AnnouncementType)}
            aria-label="Announcement type"
          >
            {TYPE_KEYS.map((k) => (
              <option key={k} value={k}>
                {TYPE_STYLES[k].label}
              </option>
            ))}
          </SelectField>
          <input
            data-testid="hub-announcement-text"
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Announcement text"
            aria-label="Announcement text"
            className="min-w-[180px] flex-1 rounded-[9px] border border-sage-300/50 bg-white px-2.5 py-2 text-[13px] text-ink"
          />
          <DateInput
            data-testid="hub-announcement-date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Event date"
            size="compact"
          />
          <SelectField size="compact"
            data-testid="hub-announcement-recur"
            value={recurrence}
            onChange={(e) => setRecurrence(e.target.value as AnnouncementRecurrence)}
            aria-label="Repeat"
          >
            {RECURRENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </SelectField>
          <button
            type="button"
            data-testid="hub-announcement-add-btn"
            onClick={handleAdd}
            disabled={addDisabled}
            className="rounded-[9px] bg-sage-800 px-4 py-2 text-[13px] font-semibold text-white hover:bg-sage-700 disabled:opacity-50"
          >
            Add
          </button>
        </div>
      )}
    </section>
  );
}
